import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { EventService } from 'src/app/core/services/event.service';
import { Globals } from 'src/app/globals/globals';
import { environment } from 'src/environments/environment';
import { OntologyService } from 'src/app/core/services/ontology.service';
import { OntMyPack } from 'src/app/core/models/ontology/ontology.model';
import { canSeeReviewQueue, openReviewItems } from 'src/app/pages/ontology/ontology-display';

export interface MenuItem {
    label: string;
    icon: string;
    route?: string;
    permission?: string | string[];
    showIf?: () => boolean;
    /** Live count rendered next to the label. Nothing renders at 0 or null. */
    badge?: () => number | null;
}

export interface MenuGroup {
    label: string;
    icon: string;
    children: MenuItem[];
}

export interface MenuDivider {
    divider: true;
}

export type MenuEntry = MenuItem | MenuGroup | MenuDivider;

@Component({
    selector: 'app-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss'],
    standalone: false
})

/**
 * Sidebar Component
 */
export class SidebarComponent implements OnInit, AfterViewInit, OnDestroy {

    queuesValue: boolean | undefined;
    isOnprem: boolean;
    showDevPages: boolean = false;
    activeGroup: MenuGroup | null = null;
    flyoutTop: number = 0;

    menuEntries: MenuEntry[] = [];

    /** Packs the user is a listed maintainer of: they gate "Review queue" (LC-23). */
    ontologyMyPacks: OntMyPack[] = [];
    private ontologyPacksFor: string | null = null;
    /** Released on destroy: a rebuilt layout left the old sidebar fetching on every navigation. */
    private navigationEnds: Subscription;

    constructor(
        private router: Router,
        private eventService: EventService,
        public globals: Globals,
        private ontology: OntologyService
    ) {
        this.navigationEnds = this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                this.closeMobileSidebar();
                this.activeGroup = null;
                this.refreshOntologyPacks(event.urlAfterRedirects.includes('/ontology/'));
            }
        });
    }

    /** Re-read on a workspace change, and on every ontology page so the open-item count follows decisions. */
    refreshOntologyPacks(force = false): void {
        const ws = this.globals.WorkSpaceID || null;
        if (!ws || (!force && ws === this.ontologyPacksFor)) return;
        this.ontologyPacksFor = ws;
        this.ontology.getMyPacks().subscribe((res: any) => {
            if (this.ontologyPacksFor !== ws) return;
            this.ontologyMyPacks = res?.flag && Array.isArray(res.data) ? res.data : [];
        }, () => {
            if (this.ontologyPacksFor === ws) this.ontologyMyPacks = [];
        });
    }

    ngOnInit(): void {
        this.isOnprem = !('onprem' in environment) || environment.onprem == undefined || environment.onprem == null || environment.onprem == false;
        const host = window.location.hostname;
        this.showDevPages = host === 'localhost' || host === 'tf.hyperthread.in';
        this.buildMenu();
        this.refreshOntologyPacks();
    }

    ngOnDestroy(): void {
        this.navigationEnds.unsubscribe();
    }

    /** The badge number for an item, or null when it has none / is at zero. */
    badgeCount(item: MenuItem): number | null {
        if (!item.badge) return null;
        const count = item.badge();
        return count && count > 0 ? count : null;
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            const currentUrl = this.router.url;
            if (currentUrl.endsWith('/cloudautomation') ||
                currentUrl.endsWith('/cloudautomation/') ||
                !currentUrl.includes('/cloudautomation/')) {
                this.router.navigate([
                    '/' + this.globals.TenantUniqueKey + '/cloudautomation/newdashboard'
                ]);
            }
        }, 100);
    }

    buildMenu(): void {
        this.menuEntries = [
            // The revamped dashboard is now THE dashboard. The old one
            // (pages/dashboard/) is no longer listed here, but its route is still
            // registered so a bookmarked /dashboard URL keeps working and it stays
            // available as a fallback — see pages-routing.module.ts.
            { label: 'Dashboard', icon: 'dripicons-graph-pie', route: 'newdashboard', permission: 'Dashboard_View' },

            { divider: true },

            { label: 'Flows', icon: 'bx bx-sitemap', route: 'new-flows', permission: 'Flow_View' },
            { label: 'Agents', icon: 'bx bxs-bolt', route: 'agent', permission: 'Agent_View' },
            // AI Agents V3 P6-WP9: the staff Chat page ("Use agents").
            { label: 'Chat', icon: 'bx bx-chat', route: 'chat', permission: 'Agent_Use' },
            // P6-WP8 (§1bi.2): everyone with "Use agents" can have notes. Order (L6): Agents, Chat (WP9), My memories.
            { label: 'My memories', icon: 'bx bx-brain', route: 'agent/my-memories', permission: 'Agent_Use' },
            // Tool credential hardening item 8 phase D: the workspace's third-party MCP servers.
            { label: 'MCP Servers', icon: 'bx bx-plug', route: 'agent/mcp-servers', permission: ['MCPServers_View', 'MCPServers_Manage'] },
            { label: 'ExtractIQ', icon: 'bx bxs-file-find', route: 'extractiq', permission: 'DataExtraction_Manage' },
            { label: 'Knowledge Search', icon: 'bx bx-book-open', route: 'enterprisesearch', permission: ['KnowledgeBaseSearch_View', 'KnowledgeBase_Create'] },

            { divider: true },

            {
                label: 'Operations',
                icon: 'bi bi-display',
                children: [
                    { label: 'Instances', icon: 'dripicons-folder', route: 'instances', permission: 'Instance_View' },
                    { label: 'Bots', icon: 'bi bi-robot', route: 'bots', permission: 'Bot_View' },
                    { label: 'Logs', icon: 'dripicons-browser', route: 'logs', permission: 'BotLogs_View' },
                    { label: 'Web Flow Runners', icon: 'bi bi-globe', route: 'webflowrunner', permission: 'RemoteRunner_View' },
                ]
            },

            {
                label: 'Automation',
                icon: 'bi bi-shuffle',
                children: [
                    { label: 'Jobs', icon: 'dripicons-user', route: 'jobs', permission: 'Job_View' },
                    { label: 'Queues', icon: 'bi bi-card-checklist', route: 'queues', permission: ['Queue_View', 'QueuePriority_View', 'QueueCustomStatus_View'] },
                    { label: 'Triggers', icon: 'dripicons-power', route: 'triggers', permission: 'Trigger_View' },
                    { label: 'Configuration', icon: 'dripicons-toggles', route: 'configuration', permission: 'Configuration_View' },
                    { label: 'Libraries', icon: 'dripicons-briefcase', route: 'libraries', permission: 'Library_View' },
                    { label: 'JSON Canvas Tester', icon: 'bi bi-filetype-json', route: 'json-canvas-tester', showIf: () => this.showDevPages },
                ]
            },

            {
                label: 'AI & Intelligence',
                icon: 'bi bi-cpu',
                children: [
                    { label: 'Extraction Models', icon: 'bi bi-collection', route: 'customermodel', permission: 'DataExtraction_Manage' },
                    { label: 'OCR', icon: 'dripicons-crosshair', route: 'ocr', permission: 'OCRExtraction_Manage' },
                    { label: 'AI Models', icon: 'bx bx-brain', route: 'modelconfig', permission: 'DataExtraction_Manage', showIf: () => this.globals.showModelConfig },
                ]
            },

            {
                label: 'HITL',
                icon: 'bi bi-clipboard-data',
                children: [
                    { label: 'Task Center', icon: 'bi bi-clipboard-data', route: 'taskcenter', permission: 'TaskCenter_View' },
                    { label: 'Forms', icon: 'mdi mdi-view-list-outline', route: 'formbuilder', permission: 'Form_View' },
                    { label: 'Validation', icon: 'dripicons-flashlight', route: 'validationtask', permission: 'DataExtraction_Manage' },
                ]
            },

            {
                label: 'Data',
                icon: 'bi bi-database',
                children: [
                    { label: 'Data Tables', icon: 'bi bi-file-ruled', route: 'createtable', permission: 'DynamicTable_View' },
                    { label: 'Knowledge Base', icon: 'pi pi-book', route: 'knowledgebase', permission: 'KnowledgeBase_View' },
                    // KB Reports live under Data rather than beside Dashboard: they report ON
                    // the Knowledge Base, and the reader who wants them is already here. The
                    // Dashboard's KB tiles link across to the matching report.
                    { label: 'Reports', icon: 'bi bi-graph-up', route: 'reports', permission: 'KbReports_View' },
                    // Metadata (enrichment) review has NO menu entry of its own: the entry
                    // point is the Knowledge Base datapage, where a NeedsReview document
                    // carries a "Review Metadata" action (P6 D2, amended 2026-07-28).
                ]
            },

            // Ontology (Addendum 2 §6). Ontology_Manage implies Ontology_View. The review
            // queue is for listed maintainers of a pack, with or without Ontology_View
            // (LC-23), with its open-item count as the badge.
            {
                label: 'Ontology',
                icon: 'bi bi-diagram-3',
                children: [
                    { label: 'Entities', icon: 'mdi mdi-shape-outline', route: 'ontology/entities', permission: ['Ontology_View', 'Ontology_Manage'] },
                    { label: 'Review queue', icon: 'mdi mdi-inbox-arrow-down-outline', route: 'ontology/queue',
                      showIf: () => canSeeReviewQueue(this.ontologyMyPacks), badge: () => openReviewItems(this.ontologyMyPacks) },
                    { label: 'Packs', icon: 'mdi mdi-package-variant-closed', route: 'ontology/packs', permission: ['Ontology_View', 'Ontology_Manage'] },
                    { label: 'Change history', icon: 'mdi mdi-history', route: 'ontology/changes', permission: ['Ontology_View', 'Ontology_Manage'] },
                    { label: 'Settings', icon: 'mdi mdi-cog-outline', route: 'ontology/settings', permission: 'Ontology_Manage' },
                ]
            },

            {
                label: 'Security',
                icon: 'mdi mdi-shield-key-outline',
                children: [
                    { label: 'Vault', icon: 'mdi mdi-shield-key-outline', route: 'credentialstore', permission: 'Vault_View' },
                    { label: 'API Keys', icon: 'pi pi-key', route: 'apikeys', permission: 'APIKeys_View' },
                    { label: 'App Tokens', icon: 'bi bi-database', route: 'apptokens', permission: 'AppTokens_View' },
                ]
            },

            {
                label: 'Licensing',
                icon: 'bi bi-clock-history',
                children: [
                    { label: 'Consumption History', icon: 'bi bi-clock-history', route: 'consumtionhistory', permission: 'ConsumptionHistory_View' },
                ]
            },

            {
                label: 'Settings', icon: 'dripicons-gear', route: 'settings',
                permission: ['Workspace_View', 'TenantUsers_View', 'Roles_View']
            },
        ];
    }

    isGroup(entry: MenuEntry): entry is MenuGroup {
        return 'children' in entry;
    }

    isDivider(entry: MenuEntry): entry is MenuDivider {
        return 'divider' in entry;
    }

    isVisible(item: MenuItem): boolean {
        if (item.showIf && !item.showIf()) return false;
        if (!item.permission) return true;
        if (Array.isArray(item.permission)) {
            return item.permission.some(p => this.globals.workspaceActions.includes(p));
        }
        return this.globals.workspaceActions.includes(item.permission);
    }

    isGroupVisible(group: MenuGroup): boolean {
        return group.children.some(c => this.isVisible(c));
    }

    getVisibleChildren(group: MenuGroup): MenuItem[] {
        return group.children.filter(c => this.isVisible(c));
    }

    isRouteActive(route: string): boolean {
        return this.router.url.includes('/' + route);
    }

    isGroupActive(group: MenuGroup): boolean {
        return group.children.some(c => c.route && this.isRouteActive(c.route));
    }

    /**
     * Hover handler — desktop only. In the phone/tablet drawer (≤991px) it is
     * ignored: a tap fires a synthetic `mouseenter` immediately followed by
     * `click`, so this opened the group and toggleFlyout() closed it again in
     * the same tap, and the groups could not be expanded at all. Same on any
     * touch-only screen, whatever its width.
     */
    showFlyout(group: MenuGroup, event: MouseEvent): void {
        if (this.tapOnly) { return; }
        this.openFlyout(group, event);
    }

    private get tapOnly(): boolean {
        if (typeof window === 'undefined' || !window.matchMedia) { return false; }
        return window.matchMedia('(max-width: 991px)').matches
            || window.matchMedia('(hover: none)').matches;
    }

    private openFlyout(group: MenuGroup, event: MouseEvent): void {
        this.activeGroup = group;
        const target = event.currentTarget as HTMLElement;
        const rect = target.getBoundingClientRect();
        // Anchor the flyout to the hovered item, but clamp so it never runs off the
        // bottom of the viewport — groups near the end of the dock (e.g. Security)
        // would otherwise open downward and clip their last items (App Tokens, …).
        // Estimate the panel height from its child count; the CSS max-height +
        // overflow-y is the backstop if the estimate is ever short.
        const count = this.getVisibleChildren(group).length;
        const HEADER_PX = 35;        // header + panel top padding
        const ITEM_PX = 40;          // per item incl. row gap (generous)
        const CHROME_PX = 16;        // borders + bottom padding
        const TOP_MARGIN_PX = 76;    // keep clear of the fixed topbar (~70px)
        const BOTTOM_MARGIN_PX = 12;
        const estHeight = HEADER_PX + count * ITEM_PX + CHROME_PX;
        const maxTop = window.innerHeight - estHeight - BOTTOM_MARGIN_PX;
        this.flyoutTop = Math.max(TOP_MARGIN_PX, Math.min(rect.top, maxTop));
    }

    /** Click/tap toggle — tapping the same group again closes the flyout. */
    toggleFlyout(group: MenuGroup, event: MouseEvent): void {
        event.stopPropagation();
        if (this.activeGroup === group) {
            this.activeGroup = null;
            return;
        }
        this.openFlyout(group, event);
    }

    hideFlyout(): void {
        this.activeGroup = null;
    }

    navigateTo(route: string): void {
        this.activeGroup = null;
        this.router.navigate([
            '/' + this.globals.TenantUniqueKey + '/cloudautomation/' + route
        ]);
    }

    onQueuesList(value: boolean) {
        localStorage.setItem('queuesBtn', JSON.stringify(value));
        this.queuesValue = value;
        this.eventService.updateQueuesValue(this.queuesValue);
        if (value) {
            this.router.navigate([
                '/' +
                this.globals.TenantUniqueKey +
                '/cloudautomation' +
                '/' +
                'queues',
            ]);
        }
    }

    getClass() {
        if (this.router.url.endsWith("itempriority") || this.router.url.endsWith("queues") || this.router.url.endsWith("customstatus"))
            return true;
    }

    closeMobileSidebar() {
        if (window.innerWidth < 992) {
            document.body.classList.remove('sidebar-enable');
        }
    }
}
